import { createStyles, withStyles } from '@mui/styles'
import React, { useState, useEffect } from 'react'
import { t } from '../../../infrastructure/i18nextHelper'
import { Paper, Typography } from '@mui/material'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import * as api from '../../../infrastructure/api'
import moment from 'moment'
import guid, { Guid } from '../../../infrastructure/guid'
import { hasClaim} from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { ColumnDescriptor, DatePicker, DateRangePicker, TableItem, TextField } from '../../common/customComponents'
import { MasterDataShell, MasterDataItem } from './masterDataShell'
import { UserSettingsContextContainer } from '../../../infrastructure/shell/userSettings/userSettingsStore'
import { importDialog } from '../../common/import'

type QuotationCalendar = {
    id: Guid,
    office: string,
    date: string,
}

type QuotationCalendarFilters = {
    office: string | null,
    dateFrom: string | null,
    dateTo: string | null
}

let emptyFilters: QuotationCalendarFilters = {
    office: null,
    dateFrom: null,
    dateTo: null
}

let zero = (): QuotationCalendar => {
    return {
        id: guid.empty,
        office: '',
        date: '',
    }
}

let toTableItem = (quotationCalendar: QuotationCalendar): TableItem<MasterDataItem<QuotationCalendar>> => {
    return {
        id: quotationCalendar.id,
        office: quotationCalendar.office,
        date: quotationCalendar.date,
        isModified: false
    }
}

let label = (index: string) => t('admin.masterdata.quotationCalendar.' + index)

function QuotationCalendarMasterData({ classes }: MuiProps) {
    let settings = UserSettingsContextContainer.useContainer()
    let [quotationCalendars, setQuotationCalendars] = useState<TableItem<QuotationCalendar>[]>([])
    let [filters, setFilters] = useState<QuotationCalendarFilters>(emptyFilters)
  
    let isManager = hasClaim(Claims.MasterdataQuotationCalendarManager)

    let load = async () => {
        let quotationCalendars = await api.get<QuotationCalendar[]>('masterdata/quotationCalendar')
        setQuotationCalendars(quotationCalendars.map(toTableItem))
    }

    let getItems = () => applyFilters(quotationCalendars, filters).map(toTableItem)

    let applyFilters = (quotationCalendars: QuotationCalendar[], filters: QuotationCalendarFilters): QuotationCalendar[] => {
        if (filters.office)
            quotationCalendars = quotationCalendars
                .filter(x => x.office?.toLowerCase().contains(filters.office!.toLowerCase()))
        
        if (filters.dateFrom) {
            quotationCalendars = quotationCalendars.filter(x =>
            moment(x.date).isSameOrAfter(moment(filters.dateFrom), 'day')
            )
        }

        if (filters.dateTo) {
            quotationCalendars = quotationCalendars.filter(x =>
            moment(x.date).isSameOrBefore(moment(filters.dateTo), 'day')
            )
        }

        return quotationCalendars
    }

    useEffect(() => { let effect = async () => { await load() }; effect() }, [])

    let onDeleteQuotationCalendar = async (ids: Guid[]) => {
        if (ids.length === 0) return false
        await api.del('masterdata/quotationCalendar', ids)
        await load()
        return true
    }

    let onSaveQuotationCalendar = async (item: QuotationCalendar) => {
        await api.post('masterdata/quotationCalendar', item)
        await load()
        return true
    }

    let dateFormat = settings.getDateFormat()

    let columns: ColumnDescriptor<TableItem<MasterDataItem<QuotationCalendar>>>[] = [
        {
            name: label('office'), value: x => x.office,
            columnFilter: { value: filters.office ?? '', onChange: (value: string) => setFilters({ ...filters, office: value }) }
        },
        { name: label('date'), value: x => moment(x.date).format(dateFormat) }
    ]

    let exportExcel = async () => {
        let from = filters.dateFrom ;
        let to   = filters.dateTo ;
        let filename = `QuotationCalendar_${moment().format('YYYY-MM-DD-HH-mm-ss')}_${guid.createNew().substring(0, 4)}.xlsx`
        await api.post('masterdata/quotationCalendar/export', 
        {
            filename,
            dateFrom: from,
            dateTo: to
        });
        await api.openDownload(filename)
    }

    let onImportExcel = (file: Blob) => {
        let uploadUrl = 'masterdata/quotationCalendar/import'
        api.upload(uploadUrl, file, 'quotationCalendar', { withReport: 'dialog' })
           .then(result => {
                importDialog.openFromCorrelationId(result.correlationId)
                load(); 
        })
    }

    let quotationCalendarName = label('name')

    return (
        <div className={classes.container}>
            <MasterDataShell
                headerLabel={quotationCalendarName}
                itemLabel={quotationCalendarName}
                isManager={isManager}
                onExportExcel={exportExcel}
                onImportExcel={onImportExcel}
                dateFilter={filters.dateFrom}
                onDateFilter={(d) => setFilters({ ...filters, dateFrom: d })}
                dateFilterTo={filters.dateTo}
                onDateFilterTo={(d) => setFilters({ ...filters, dateTo: d })}
                onNew={() => zero()}
                onDelete={onDeleteQuotationCalendar}
                onSave={onSaveQuotationCalendar}
                items={getItems()}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                            <TextField label={label('office')}
                                                            disabled={!isManager}
                                                            text={selectedItem.office}
                                                            onChange={event => setSelectedItem({ ...selectedItem, office: event.target.value })} />
                            <DatePicker date={selectedItem.date}
                                label={label('date')}
                                setDate={newDate => setSelectedItem({ ...selectedItem, date: newDate })} />
                        </>
                    )}
            </MasterDataShell>
        </div>)
}


let styles = (_) => createStyles({ container: { height: '100%' } })

export default withStyles(styles, muiOptions)(QuotationCalendarMasterData)